import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAsksData } from '../../services/asksService';
import { isAuthenticated } from '../../utils/api';
import { useAuth } from "../../contexts/AuthContext";
import { DashboardTabContext } from "../layout/Layout";

const OverviewSection = ({ feeds = [] }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [shopAsks, setShopAsks] = useState([]);
  const [loadingAsks, setLoadingAsks] = useState(false);
  const [asksError, setAsksError] = useState(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { dashboardTab, setDashboardTab } = useContext(DashboardTabContext);

  const hasFeeds = feeds.length > 0;
  const hasNoProducts = !currentUser?.hasProduct;

  const renderAddItem = () => (
    hasNoProducts ? (
      <>
        <p className="text-center text-sm text-gray-600 mb-3">
          Add Product to your store, discover more customers
        </p>
        <button
          onClick={() => navigate("/store#create")}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition"
        >
          + Add Item
        </button>
      </>
    ) : (
      <button
        onClick={() => navigate("/store#create")}
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition"
      >
        + Add Item
      </button>
    )
  );

  // Load shop asks on component mount for desktop, and when tab changes for mobile
  useEffect(() => {
    loadShopAsks();
  }, []);

  // Load shop asks when switching to Shop Asks tab (e.g. from mobile header)
  useEffect(() => {
    if (dashboardTab === "upnext") {
      loadShopAsks();
    }
  }, [dashboardTab]);

  const loadShopAsks = async () => {
    setLoadingAsks(true);
    setAsksError(null);
    try {
      const result = await fetchAsksData({ page: 1, limit: 50 });
      console.log(result, "ASKS RESULT") // Debug log to check the structure of the result
      // fetchAsksData returns asks already normalized by formatAsk
      if (result.success && Array.isArray(result.data)) {
        const formattedAsks = result.data;
        setShopAsks(formattedAsks);
      } else {
        console.error('Failed to load asks:', result.error);
        setAsksError(result.error || 'Failed to load shop asks');
        setShopAsks([]);
      }
    } catch (err) {
      console.error('Exception loading asks:', err);
      setAsksError(err.message);
      setShopAsks([]);
    } finally {
      setLoadingAsks(false);
    }
  };

  const handleFeedClick = (e) => {
    e.preventDefault();
    setShowPopup(true);
  };

  const renderFeedCard = (feed, i) => (
    <div key={i} className="border-b border-l border-r border-gray-200 px-4 py-5 xl:px-5 transition md:w-[100vw] sm:w-[100vw] w-[100%] max-w-[700px] m-auto">
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <img
            src={feed.authorAvatar || "/assets/default-avatar.png"}
            alt={feed.author}
            className="w-10 h-10 rounded-full object-cover"
          />
        </div>

        {/* Post Body */}
        <div className="flex-1 min-w-0">
          {/* Author + Meta */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 text-[15px]">
                {feed.author}
              </span>
              {feed.username && (
                <span className="text-gray-500 text-sm">@{feed.username}</span>
              )}
            </div>

            {/* Options */}
            <button
              onClick={handleFeedClick}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 12a.75.75 0 110-1.5.75.75 0 010 1.5zm5.25 0a.75.75 0 110-1.5.75.75 0 010 1.5zm5.25 0a.75.75 0 110-1.5.75.75 0 010 1.5z"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap mt-1">
            {feed.content}
          </p>

          {/* Media */}
          {feed.image && (
            <div className="mt-3 rounded-xl overflow-hidden border border-gray-200">
              <img
                src={feed.image}
                alt="feed-media"
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {feed.video && (
            <div className="mt-3 rounded-[20px] overflow-hidden border border-gray-200">
              <video
                src={feed.video}
                loop
                controls
                autoPlay
                muted
                playsInline
                className="w-full h-auto object-cover rounded-xl"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between mt-4 text-gray-500 text-sm select-none max-w-md">
            <button onClick={handleFeedClick} className="flex items-center gap-2 group">
              <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 24 24"
                className="w-5 h-5 group-hover:text-purple-500 transition">
                <path fill="currentColor"
                  d="M10 3h4a8 8 0 1 1 0 16v3.5c-5-2-12-5-12-11.5a8 8 0 0 1 8-8Zm2 14h2a6 6 0 0 0 0-12h-4a6 6 0 0 0-6 6c0 3.61 2.462 5.966 8 8.48V17Z" />
              </svg>
              <span className="group-hover:text-purple-500 transition">{feed.messages || 0}</span>
            </button>

            <button onClick={handleFeedClick} className="flex items-center gap-2 group">
              <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 24 24"
                className="w-5 h-5 group-hover:text-blue-500 transition">
                <path fill="currentColor"
                  d="M12.558 2.982a1.75 1.75 0 0 0-1.122 0L4.66 5.277a1.19 1.19 0 0 0-.817 1.019c-.337 4.226.248 7.351 1.637 9.712c1.344 2.285 3.494 3.946 6.517 5.184c3.026-1.238 5.178-2.899 6.522-5.184c1.39-2.36 1.975-5.486 1.638-9.712a1.19 1.19 0 0 0-.817-1.019zm-1.603-1.42a3.25 3.25 0 0 1 2.084-.001l6.782 2.295a2.69 2.69 0 0 1 1.83 2.32c.353 4.41-.24 7.876-1.839 10.593c-1.604 2.725-4.161 4.598-7.54 5.929a.75.75 0 0 1-.55 0c-3.376-1.331-5.932-3.204-7.535-5.929c-1.598-2.717-2.191-6.184-1.839-10.593a2.69 2.69 0 0 1 1.83-2.32z" />
              </svg>
              <span className="group-hover:text-blue-500 transition">{feed.vouches || 0}</span>
            </button>

            <button onClick={handleFeedClick} className="flex items-center gap-2 group">
              <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 24 24"
                className="w-5 h-5 group-hover:text-amber-500 transition" fill="none">
                <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                  <path d="M14 10h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3H10m2-7v1m0 6v1" />
                  <path d="M17 4v1.882c0 .685.387 1.312 1 1.618s1 .933 1 1.618V18a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V9.118c0-.685.387-1.312 1-1.618s1-.933 1-1.618V4M6 4h12z" />
                </g>
              </svg>
              <span className="group-hover:text-amber-500 transition">{feed.tips || 0}</span>
            </button>

            <button onClick={handleFeedClick} className="flex items-center gap-2 group">
              <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 12 12"
                className="w-5 h-5 group-hover:text-green-500 transition">
                <path fill="currentColor"
                  d="M2.5 2h.012q.03 0 .062.005za.5.5 0 0 1 .354.146l2 2A.5.5 0 0 1 4.5 5H3v2.5a1.5 1.5 0 0 0 1.356 1.493L4.5 9H6a.5.5 0 0 1 .09.992L6 10H4.5a2.5 2.5 0 0 1-2.495-2.336L2 7.5V5H.5a.5.5 0 0 1-.354-.854l2-2l.013-.011l.039-.033l-.052.044A.5.5 0 0 1 2.5 2m6.954 7.998l.021.001l-.028-.002l-.016-.003h-.002l-.034-.005l-.046-.012l-.04-.015l-.076-.04l-.035-.024l-.005-.003l-.001-.001l-.014-.011l.01.008l.01.007l-.04-.033l-2.011-2.011a.5.5 0 0 1 .268-.847L7.5 7H9V4.5a1.5 1.5 0 0 0-1.356-1.493L7.5 3H6a.5.5 0 0 1-.09-.992L6 2h1.5a2.5 2.5 0 0 1 2.495 2.336L10 4.5V7h1.5a.5.5 0 0 1 .41.788l-.056.066L9.84 9.866A.5.5 0 0 1 9.5 10l-.068-.006z" />
              </svg>
              <span className="group-hover:text-green-500 transition">{feed.reposts || 0}</span>
            </button>

            <button onClick={handleFeedClick} className="flex items-center gap-2 group">
              <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 21 21"
                className="w-5 h-5 group-hover:transition">
                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                  d="m14.5 7.5l-3.978-4l-4.022 4m4.022-3.979V15.5m-3.022-5h-2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-2" />
              </svg>
              <span className="group-hover:transition">{feed.shares || 0}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderShopAskCard = (ask, index) => {
    return (
      <div key={ask.id || index} className="border-b border-gray-200 px-4 py-5 transition hover:bg-gray-50">
        <div className="flex gap-3">
          {/* Buyer Avatar */}
          <div className="flex-shrink-0">
            {ask.buyerLogo ? (
              <img
                src={ask.buyerLogo}
                alt={ask.buyerName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                {ask.buyerName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Ask Content */}
          <div className="flex-1 min-w-0">
            {/* Buyer Info */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 text-[15px]">
                  {ask.buyerName}
                </span>
                <span className="text-gray-500 text-sm">
                  · {new Date(ask.createdAt).toLocaleDateString()}
                </span>
                {/* {ask.qualityScore && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    parseFloat(ask.qualityScore) >= 80 ? 'bg-green-100 text-green-700' :
                    parseFloat(ask.qualityScore) >= 50 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    Score: {ask.qualityScore}
                  </span>
                )} */}
                {ask.isExpired && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    Expired
                  </span>
                )}
              </div>
              {/* <span className="text-xs text-gray-400">
                {ask.responseCount} responses
              </span> */}
            </div>

            {/* Location */}
            {ask.location && ask.location !== 'Location not specified' && (
              <div className="flex items-center gap-1 mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span className="text-xs text-gray-500">{ask.location}</span>
              </div>
            )}

            {/* Content */}
            <p className="text-[15px] leading-relaxed whitespace-pre-wrap mt-1 text-gray-800">
              {ask.messageBody || ask.description || 'Buyer posted a new request.'}
            </p>

            {/* Action Button */}
            {!ask.isExpired && (
              <button
                onClick={() => navigate(`/chats?ask=${ask.id}`)}
                className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition shadow-sm"
              >
                Respond to Ask
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUpNext = () => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Shop Asks</h3>
            <p className="text-xs text-gray-500 mt-1">Customer requests and inquiries</p>
          </div>
          <button
            onClick={loadShopAsks}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            disabled={loadingAsks}
          >
            {loadingAsks ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {loadingAsks ? (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-gray-500 mt-3 text-sm">Loading shop asks...</p>
        </div>
      ) : asksError ? (
        <div className="p-8 text-center">
          <div className="text-red-500 text-4xl mb-3">⚠️</div>
          <p className="text-gray-600 text-sm">{asksError}</p>
          <button
            onClick={loadShopAsks}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
          >
            Retry
          </button>
        </div>
      ) : shopAsks.length === 0 ? (
        <div className="p-8 text-center">
          <div className="text-gray-400 text-5xl mb-3">📭</div>
          <p className="text-gray-500 text-sm">No shop asks available at this time.</p>
          <p className="text-gray-400 text-xs mt-1">Check back later for customer requests.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
          {shopAsks.map((ask, index) => renderShopAskCard(ask, index))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Main Section */}
      <div className="flex flex-col w-full min-h-0 lg:h-screen lg:overflow-hidden">
        {/* ======= DESKTOP ======= */}
        <div className="hidden md:flex flex-1 min-h-0 h-full">
          {/* Left column: scrollable shop asks feed */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 border-r border-gray-200 dark:border-gray-800">
            {/* Feed content - scrollable */}
            <div className="flex-1 overflow-y-auto px-6 pt-4">
              <div className="max-w-2xl mx-auto">
                {renderUpNext()}
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="hidden md:block w-[360px] overflow-y-auto shrink-0">
              <div className="sticky top-0 mt-3 px-4">
                <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
                  {renderAddItem()}
                </div>
              </div>
            </div>
        </div>

        {/* ======= MOBILE ======= */}
        <div className="flex flex-col w-full md:hidden">
          <div className="flex flex-col gap-4 p-4 w-full">
            <div className="bg-white p-4 rounded-2xl shadow-sm mb-2">
              {renderAddItem()}
            </div>
            {renderUpNext()}
          </div>
        </div>
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowPopup(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-[90%] text-center shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">
              Feed is coming soon
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              This will be where you'll see people's intent requests related to your
              products and services. You'll be able to pouch, engage, and interact with users.
            </p>
            <button
              onClick={() => setShowPopup(false)}
              className="mt-2 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default OverviewSection;